import {
  AdviceProduct,
  AdviceProductDetailsBackendValues,
  AdviceProductDetailsFormValues,
  AdvisorValues,
  CartAdviceValues,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  AdvicePriceFormValues,
  OneTimePaymentAdvisorChoice,
} from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/interfaces";
import {
  ADVICE_NAMES,
  Advice,
  AdvicePriceCalculationRequest,
  AdvicePriceCalculationResult,
  AdviceProductChangeType,
  AdviceProductCustomerChangeTypes,
  AdviceResponse,
  AdviceState,
  OperationType,
} from "Store/advice/interfaces";
import {
  ADVICE_RESOURCE_ROOT,
  CART_RESOURCE_ROOT,
  OPEN_ADVICE_RESOURCE_ROOT,
} from "Utils/apiUtils";
import {
  recordAdviceCartIfRecording,
  recordAdviceIfRecording,
  recordAdvicePriceCalculationIfRecording,
  recordPriceCalculationIfRecording,
} from "Utils/fixtureRecordingUtils";
import { createRequest, getError } from "Utils/requestUtils";
import {
  SelectEffect,
  all,
  call,
  put,
  select,
  takeLatest,
} from "redux-saga/effects";
import {
  advicePriceCalculationRequestStart,
  advicePriceCalculationRequestSucceeded,
  customerChangeProductUpdate,
  getAdviceStart,
  getAdviceSucceeded,
  operationFailed,
  resetChangedByCustomer,
  updateChangedByCustomer,
  updateInitialPriceCalculationRequest,
  updateInitialPriceCalculationRequestPrices,
} from "./slice";
import {
  adviceDetailsFormValuesSelector,
  advicePriceFormValuesSelector,
  adviceSelector,
  cartAdviceSelector,
} from "./stateSelectors";
import {
  createOldPriceCalculationRequest,
  formValuesDiff,
  getAdviceUpdatePayloadWithPrices,
  isAdviced,
  removeOpenForAdvisors,
} from "./utils";

import { ADVICE_PRICE_STEP } from "Components/Features/CheckPrice/Products/Advice";
import { mapAdviceDataToDetailsFormData } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/utils";
import { mapAdviceCoverageNameToInsuranceName } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { removeWhitespace } from "Formatting/index";
import { isAdvisorSelector } from "Store/advisor/advisorSelectors";
import { Cart } from "Store/cart/cartInterfaces";
import { cartSelector } from "Store/cart/cartSagas";
import { fetchCartSucceeded } from "Store/cart/cartSlice";
import { ReduxState } from "Store/rootReducer";
import { activateStep } from "Store/steps/stepsSlice";
import { UserIdentificationData } from "Store/userIdentification";
import { userIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { nullableFormBooleanToNullableBoolean } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import isEmpty from "lodash.isempty";
import { OldPriceCalculationRequest } from "./productPriceRequestMappers";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* getAdvice({ payload }: ReturnType<typeof getAdviceStart>) {
  try {
    const isAdvisor: boolean = yield select(isAdvisorSelector);
    const { data } = yield call(createRequest, {
      method: "post",
      requestBody: payload,
      url: isAdvisor ? ADVICE_RESOURCE_ROOT : OPEN_ADVICE_RESOURCE_ROOT,
      open: !isAdvisor,
    });
    recordAdviceIfRecording(data);

    const { payload: insuranceAdvices } = data as AdviceResponse;
    yield put(getAdviceSucceeded(insuranceAdvices));

    yield call(handleInitialAdvice, insuranceAdvices);
    yield put(advicePriceCalculationRequestStart(insuranceAdvices));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.SEND_GET_ADVICE_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* handleInitialAdvice(insuranceAdvices: Advice) {
  const isAdvisor: boolean = yield select(isAdvisorSelector);
  const advisorValues: AdvisorValues = yield call(getAdvisorValues);
  const adviceDetailsFormValues: AdviceProductDetailsFormValues = yield select(
    adviceDetailsFormValuesSelector,
  );
  const cartAdvice: CartAdviceValues | undefined = yield select(
    cartAdviceSelector,
  );
  const didformValuesChange =
    cartAdvice?.adviceProductValues &&
    !formValuesDiff(
      isAdvisor,
      cartAdvice.adviceProductValues,
      adviceDetailsFormValues,
      advisorValues,
    );
  if (didformValuesChange) {
    yield put(
      updateChangedByCustomer({
        insuranceAdvices,
        cartAdviceValues: cartAdvice,
        isAdvisor,
      }),
    );
  } else {
    yield put(resetChangedByCustomer());
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* advicePriceCalculationRequest({
  payload,
}: ReturnType<typeof advicePriceCalculationRequestStart>) {
  try {
    const adviceDetailsFormValues: AdviceProductDetailsFormValues =
      yield select(adviceDetailsFormValuesSelector);
    const advicePriceCalculationRequests: AdvicePriceCalculationRequest =
      yield select(
        (state: ReduxState): AdvicePriceCalculationRequest =>
          state.advice?.advicePriceCalculationRequests,
      );
    const cartAdvice: CartAdviceValues | undefined = yield select(
      cartAdviceSelector,
    );
    const cart: Cart | null = yield select(cartSelector);
    const isAdvisor: boolean = yield select(isAdvisorSelector);

    const oldPriceCalculationRequests: OldPriceCalculationRequest[] = [];
    yield all(
      Object.keys(payload.advice)
        .filter((key: ADVICE_NAMES): key is ADVICE_NAMES =>
          isAdviced(payload.advice[key]),
        )
        .map((key) => {
          const { oldPriceCalculationRequest, productCheckedToPurchase } =
            createOldPriceCalculationRequest(
              adviceDetailsFormValues,
              key,
              cart,
              payload,
              advicePriceCalculationRequests,
              isAdvisor,
            );
          oldPriceCalculationRequests.push(oldPriceCalculationRequest);
          return put(
            updateInitialPriceCalculationRequest({
              adviceProductChangeType: AdviceProductChangeType.INITIAL_SETUP,
              insuranceName: oldPriceCalculationRequest.insuranceName,
              productCheckedToPurchase: isDefined(productCheckedToPurchase)
                ? productCheckedToPurchase
                : isAdviced(payload.advice[key]),
              sliderValue: oldPriceCalculationRequest.data.insuredSum,
              priceCalculationRequest: {
                productPriceUrl: removeOpenForAdvisors(
                  oldPriceCalculationRequest.productPriceUrl,
                  isAdvisor,
                ),
                insuranceName: oldPriceCalculationRequest.insuranceName,
                data: oldPriceCalculationRequest.data,
              },
            }),
          );
        }),
    );

    const {
      discountResponse,
      newCartResponse,
      newCustomerResponse: { customerRef, dateOfBirth, isFemale },
    }: UserIdentificationData = yield select(userIdentificationSelector);
    const responses: any[] = yield all(
      oldPriceCalculationRequests.map((oldPriceCalculationRequest) => {
        return call(createRequest, {
          method: "post",
          requestBody: {
            discount: discountResponse.f2100,
            r: {
              cartId: newCartResponse.cartId,
              customerRef,
              dateOfBirth,
              isFemale,
              isSmoker: false,
              ...oldPriceCalculationRequest.data,
            },
          },
          url: removeOpenForAdvisors(
            oldPriceCalculationRequest.productPriceUrl,
            isAdvisor,
          ),
          open: !isAdvisor,
        });
      }),
    );
    const priceCalculationResults: AdvicePriceCalculationResult[] =
      responses.map((res) => {
        recordAdvicePriceCalculationIfRecording(res.config.url, res.data);
        const data = res.data.configurations;
        const configurationName: string =
          Object.keys(data).find((key) => key) ?? "";
        return { configurationName, ...data[configurationName] };
      });

    yield put(advicePriceCalculationRequestSucceeded(priceCalculationResults));
    if (isEmpty(advicePriceCalculationRequests.changedByCustomer)) {
      yield all(
        priceCalculationResults.map((priceCalculationResult) => {
          const insuranceName = mapAdviceCoverageNameToInsuranceName(
            priceCalculationResult.configurationName,
          );
          const monthlyPrice =
            priceCalculationResult.prices[0].monthlyPricePrecise;
          return put(
            updateInitialPriceCalculationRequestPrices({
              insuranceName,
              insuranceMonthlyPrice: monthlyPrice,
            }),
          );
        }),
      );
    } else if (
      cartAdvice?.adviceInitialRequest &&
      !isEmpty(cartAdvice.adviceInitialRequest)
    ) {
      yield all(
        cartAdvice.adviceInitialRequest.map((adviceData) => {
          const { insuranceName, insuranceMonthlyPrice } = adviceData;
          return put(
            updateInitialPriceCalculationRequestPrices({
              insuranceName,
              insuranceMonthlyPrice,
            }),
          );
        }),
      );
    }

    yield put(activateStep(ADVICE_PRICE_STEP.id));
  } catch (e) {
    // TODO - If one product-price-request fails the whole operation fails. Shouldn't this be handled in a better way?
    yield put(
      operationFailed({
        operationType: OperationType.SEND_ADVICE_PRICE_CALCULATION_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* customerPriceCalculationRequest({
  payload,
}: ReturnType<typeof customerChangeProductUpdate>) {
  if (
    AdviceProductCustomerChangeTypes.includes(payload.adviceProductChangeType)
  ) {
    try {
      const userIdentificationData: UserIdentificationData = yield select(
        userIdentificationSelector,
      );
      const isAdvisor: boolean = yield select(isAdvisorSelector);
      const { data } = yield call(createRequest, {
        method: "post",
        requestBody: {
          discount: userIdentificationData.discountResponse.f2100,
          r: {
            cartId: userIdentificationData.newCartResponse.cartId,
            customerRef: userIdentificationData.newCustomerResponse.customerRef,
            dateOfBirth: userIdentificationData.newCustomerResponse.dateOfBirth,
            isFemale: userIdentificationData.newCustomerResponse.isFemale,
            isSmoker: false,
            ...payload.priceCalculationRequest.data,
          },
        },
        url: removeOpenForAdvisors(
          payload.priceCalculationRequest.productPriceUrl,
          isAdvisor,
        ),
        open: !isAdvisor,
      });
      recordPriceCalculationIfRecording(
        payload.priceCalculationRequest.productPriceUrl,
        data,
      );

      const configurations = data.configurations;
      const configurationName: string =
        Object.keys(configurations).find((key) => key) ?? "";
      const priceCalculationResult: AdvicePriceCalculationResult = {
        configurationName,
        ...configurations[configurationName],
      };

      yield put(
        advicePriceCalculationRequestSucceeded([priceCalculationResult]),
      );
    } catch (e) {
      yield put(
        operationFailed({
          operationType: OperationType.SEND_CUSTOMER_PRICE_CALCULATION_REQUEST,
          response: getError(e),
        }),
      );
    }
  } else {
    yield put(advicePriceCalculationRequestSucceeded([]));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* handleCartAdviceUpdateRequest() {
  const isAdvisor: boolean = yield select(isAdvisorSelector);
  const userIdentificationData: UserIdentificationData = yield select(
    userIdentificationSelector,
  );
  const advice: AdviceState = yield select(adviceSelector);
  const adviceRequest: AdviceProductDetailsBackendValues = yield select(
    (state: ReduxState): AdviceProductDetailsBackendValues | null =>
      state.advice.adviceRequest,
  );
  const adviceProducts: AdviceProduct[] = yield select(
    (state: ReduxState): AdviceProduct[] | undefined =>
      state.cart.current?.advice?.adviceProducts,
  );
  const adviceDocumentId: string | undefined = yield select(
    (state: ReduxState): string | undefined =>
      state.advicePDF.response.current?.payload.id,
  );
  const advisorValues: AdvisorValues = yield call(getAdvisorValues);

  const adviceUpdatePayload = getAdviceUpdatePayloadWithPrices(
    isAdvisor,
    adviceProducts,
    advice,
    adviceRequest,
    advisorValues,
    adviceDocumentId,
  );

  const { data } = yield call(createRequest, {
    method: "post",
    requestBody: adviceUpdatePayload,
    url: `${CART_RESOURCE_ROOT}/${userIdentificationData?.newCartResponse?.cartId}/products/advice`,
    open: false,
  });

  recordAdviceCartIfRecording(data);
  const cartAdvice: CartAdviceValues | undefined = yield select(
    cartAdviceSelector,
  );
  if (
    cartAdvice?.adviceProductValues &&
    formValuesDiff(
      isAdvisor,
      cartAdvice?.adviceProductValues,
      mapAdviceDataToDetailsFormData(adviceRequest),
      advisorValues,
    )
  ) {
    yield put(fetchCartSucceeded(data));
  }
}

export function* getAdvisorValues(): Generator<
  SelectEffect,
  AdvisorValues,
  AdviceProductDetailsFormValues & AdvicePriceFormValues
> {
  const adviceDetailsFormValues: AdviceProductDetailsFormValues = yield select(
    adviceDetailsFormValuesSelector,
  );
  const advicePriceFormValues: AdvicePriceFormValues = yield select(
    advicePriceFormValuesSelector,
  );
  const hasAdvisorChosenCustomOneTimePayment =
    advicePriceFormValues?.disabilityOneTimePaymentAdvisorChoice ===
    OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT;
  return {
    situationComment: adviceDetailsFormValues?.situationComment,
    adviceComment: advicePriceFormValues?.adviceComment,
    disabilityPaymentAfter3Months: nullableFormBooleanToNullableBoolean(
      advicePriceFormValues?.disabilityPaymentAfter3Months,
    ),
    disabilityOneTimePaymentAdvisorChoice:
      advicePriceFormValues?.disabilityOneTimePaymentAdvisorChoice,
    disabilityOneTimePaymentAdvisorValue:
      hasAdvisorChosenCustomOneTimePayment &&
      advicePriceFormValues?.disabilityOneTimePaymentAdvisorValue
        ? Number(
            removeWhitespace(
              advicePriceFormValues?.disabilityOneTimePaymentAdvisorValue,
            ),
          )
        : undefined,
  };
}

export function* adviceSaga(): Generator {
  yield takeLatest(getAdviceStart, getAdvice);
  yield takeLatest(
    advicePriceCalculationRequestStart,
    advicePriceCalculationRequest,
  );
  yield takeLatest(
    customerChangeProductUpdate,
    customerPriceCalculationRequest,
  );
}
